/* eslint-disable react/prop-types */
import React, { useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'

import { getCustomerData, getHolders, getInsuranceIds } from 'client/contexts/Purchase/selectors'
import { useSystem } from 'src/hooks/useSystem'

import { createHoldersFromBoards } from './utils'
import { useEffectOnce } from 'react-use'
import { useMarket } from 'src/hooks/useMarket'

const CheckoutContext = React.createContext()

const CheckoutProvider = ({
  additionalProductsSelections,
  additionalProductsDismissed = {},
  boards = [],
  children,
  insuranceIds,
  insuranceQuotes,
  payNowIds,
  purchaseId,
  priceDropProtectionQuote,
}) => {
  const { apiHostName } = useSystem()
  const customerData = useSelector(getCustomerData)
  const insuranceIdsStored = useSelector(getInsuranceIds)
  const { autoNewsletterSubscribe } = useMarket()

  const count = boards.reduce((acc, board) => acc + parseInt(board.roomsNumber), 0)
  const isMultiRoom = count > 1
  const isPhysicalAddressRequired = boards.some(board => board.isPhysicalAddressRequired === true)

  const holdersStored = useSelector(getHolders)
  let holders = count === 1 ? [] : createHoldersFromBoards(count)

  if (holdersStored.length === count && holdersStored) {
    holders = holdersStored.map(holderStored => ({ ...holderStored, errors: [] }))
  }

  const selectedInsuranceIds = insuranceIdsStored?.length ? insuranceIdsStored : insuranceIds
  const boardsWihtInsurance = boards.filter(board =>
    selectedInsuranceIds.includes(board.availabilityId)
  )

  const initialInsuranceSelections = useMemo(() => {
    return boards
      .map(board => {
        if (board?.insurance?.amount > 0) {
          return {
            isInsured: selectedInsuranceIds.includes(board.availabilityId) ? true : null,
            availabilityId: board.availabilityId,
          }
        }
        return null
      })
      .filter(Boolean)
  }, [boards, selectedInsuranceIds])

  const [checkout, setCheckout] = useState({
    ...customerData,
    additionalProductsSelections,
    additionalProductsDismissed,
    hostname: apiHostName,
    boardsWihtInsurance,
    insuranceSelections: initialInsuranceSelections,
    holders,
    isPhysicalAddressRequired,
    payNowIds,
    priceDropProtectionQuote,
  })

  const [isEditingCreditCard, setIsEditingCreditCard] = useState(false)

  useEffect(() => {
    setCheckout({
      ...checkout,
      ...customerData,
      boardsWihtInsurance,
      insuranceSelections: initialInsuranceSelections,
      holders,
      subscribeNewsletter: autoNewsletterSubscribe ?? false,
    })
  }, [customerData.email]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffectOnce(() => {
    setCheckout({
      ...checkout,
      subscribeNewsletter: autoNewsletterSubscribe ?? false,
    })
  })

  const state = useMemo(
    () => ({
      boards,
      checkout,
      insuranceQuotes,
      isMultiRoom,
      purchaseId,
      priceDropProtectionQuote,
      setCheckout,
      isEditingCreditCard,
      setIsEditingCreditCard,
    }),
    [
      boards,
      checkout,
      insuranceQuotes,
      isMultiRoom,
      purchaseId,
      priceDropProtectionQuote,
      setCheckout,
      isEditingCreditCard,
      setIsEditingCreditCard,
    ]
  )

  return <CheckoutContext.Provider value={state}>{children}</CheckoutContext.Provider>
}

CheckoutProvider.propTypes = {
  additionalProductsSelections: PropTypes.object,
  boards: PropTypes.array,
  children: PropTypes.node,
  insuranceIds: PropTypes.arrayOf(PropTypes.string),
  insuranceQuotes: PropTypes.object,
  payNowIds: PropTypes.arrayOf(PropTypes.string),
  purchaseId: PropTypes.string,
  priceDropProtectionQuote: PropTypes.shape({
    key: PropTypes.string,
    value: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        fee: PropTypes.number.isRequired,
        currency: PropTypes.string.isRequired,
      })
    ),
  }),
}

export { CheckoutContext, CheckoutProvider }
